import { useState, useEffect } from 'react'
import { User, getAuth, validatePassword } from 'firebase/auth'
import { FirebaseAuth } from 'shared/services'
import { LoadingStatus, ReauthenticationVariant } from 'shared/types'
import { isPopupClosedError } from 'shared/utils/auth'
import { useAuthContext } from 'shared/components/AuthContext'
import { usePasswordPolicyWarning } from 'shared/components/PasswordPolicyContext'
import { useHistory } from 'react-router'
import { EMAIL_FOR_SIGN_IN_STORAGE_KEY, Errors, paths } from 'config'
import { useConsents } from './use-consents'
import { useTwoFactorPrompt } from './use-two-factor-prompt'
import { useMultiFactorErrorHandler } from './use-two-factor'
import { useRedirectToReauthenticationSuccess } from './use-redirect-to-reauthentication-success'
import { useSignOut } from './use-sign-out'
import {
  hasOAuthSignInProvider,
  resetViewerMode,
  OAuthProviderId,
} from 'shared/utils'

const auth = new FirebaseAuth()

const isOAuthAccountNotFound = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const message = (error as { message?: unknown }).message

  return typeof message === 'string' && message.includes('ACCOUNT_NOT_FOUND')
}

const isPasswordResetEligibilityBlocked = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const typedError = error as Record<string, unknown>
  const message =
    typeof typedError.message === 'string' ? typedError.message : ''

  return message.includes('PERMISSION_DENIED')
}

export const useAuth = () => {
  const user = useAuthContext()
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)
  const redirectToReauthenticationSuccess =
    useRedirectToReauthenticationSuccess()
  const { setLocalPreferencsByRemoteConfig } = useConsents()
  const handleMultiFactorError = useMultiFactorErrorHandler()
  const { promptIfTwoFactorDisabled } = useTwoFactorPrompt()
  const signOut = useSignOut()

  const { showPasswordPolicyWarning, hidePasswordPolicyWarning } =
    usePasswordPolicyWarning()

  const updatePasswordPolicyWarning = async (
    password: string,
    userId: string
  ) => {
    try {
      const status = await validatePassword(getAuth(), password)

      if (getAuth().currentUser?.uid !== userId) return

      if (!status.isValid) {
        showPasswordPolicyWarning()
      }
    } catch (error) {
      console.error('Password policy validation failed', error)
    }
  }

  const signIn = async (
    email: string,
    password: string,
    reauthenticate: boolean,
    reauthVariant: ReauthenticationVariant | null
  ) => {
    const onSignedIn = () => {
      if (reauthVariant !== null) {
        redirectToReauthenticationSuccess(reauthVariant)
      } else {
        hidePasswordPolicyWarning()
        const userId = getAuth().currentUser?.uid
        if (userId) void updatePasswordPolicyWarning(password, userId)
        promptIfTwoFactorDisabled()
      }
      setLocalPreferencsByRemoteConfig()
    }

    try {
      setError(null)
      setLoading(LoadingStatus.Pending)
      if (reauthenticate && reauthVariant !== null) {
        await auth.reauthenticateWithPasswordCredential(email, password)
      } else {
        resetViewerMode()
        await auth.signIn(email, password)
      }

      onSignedIn()
      setLoading(LoadingStatus.Succeeded)
      return
    } catch (error: any) {
      if (handleMultiFactorError(error, { email, onResolved: onSignedIn })) {
        setLoading(LoadingStatus.Idle)
        return
      }
      setError(error?.code || null)
      setLoading(LoadingStatus.Failed)
    }
  }

  const isAuthenticated =
    !!user.user &&
    (user.user.emailVerified || hasOAuthSignInProvider(user.user))

  return {
    user,
    isAuthenticated,
    loading,
    error,
    signIn,
    signOut,
  }
}

export const useSignInWithLink = () => {
  const { setLocalPreferencsByRemoteConfig } = useConsents()
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)
  const history = useHistory()
  const redirectToReauthenticationSuccess =
    useRedirectToReauthenticationSuccess()
  const { setError: setGlobalAuthError } = useAuthContext()
  const handleMultiFactorError = useMultiFactorErrorHandler()
  const { promptIfTwoFactorDisabled } = useTwoFactorPrompt()

  const signInWithLink = async (
    email: string,
    emailLink: string,
    reauthenticate: boolean,
    reauthVariant: ReauthenticationVariant | null
  ) => {
    const isReauthentication = reauthVariant !== null && reauthenticate
    const isLoginBeforeDeleteAccount =
      reauthVariant === ReauthenticationVariant.DELETE_ACCOUNT &&
      !reauthenticate

    const onSignedIn = async () => {
      if (
        reauthVariant !== null &&
        (isReauthentication || isLoginBeforeDeleteAccount)
      ) {
        redirectToReauthenticationSuccess(reauthVariant)
      } else {
        await auth.reloadUser()
        promptIfTwoFactorDisabled()
      }
      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY)
      setLocalPreferencsByRemoteConfig()
    }

    try {
      setLoading(LoadingStatus.Pending)
      if (isReauthentication) {
        await auth.reauthenticateWithEmailLinkCredential(email, emailLink)
      } else if (isLoginBeforeDeleteAccount) {
        // login before delete account
        await auth.signInWithEmailLink(email, emailLink)
      } else {
        // basic email login
        const methods = await auth.fetchSignInMethodsForEmail(email)
        if (methods.length === 0) {
          if (setGlobalAuthError)
            setGlobalAuthError(Errors.InvalidDeviceContext)
          return history.replace(paths.signIn)
        }
        await auth.signInWithEmailLink(email, emailLink)
      }
      await onSignedIn()
      setLoading(LoadingStatus.Succeeded)
      return
    } catch (error: any) {
      if (handleMultiFactorError(error, { email, onResolved: onSignedIn })) {
        setLoading(LoadingStatus.Idle)
        return
      }
      setError(error?.code || null)
      setLoading(LoadingStatus.Failed)
    }
  }

  return {
    loading,
    error,
    signInWithLink,
  }
}

export const useConfirmPasswordReset = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const confirmPasswordReset = async (code: string, password: string) => {
    try {
      setLoading(LoadingStatus.Pending)
      await auth.confirmPasswordReset(code, password)
      setLoading(LoadingStatus.Succeeded)
      setError(null)
    } catch (error: any) {
      setError(error?.code ?? Errors.InternalError)
      setLoading(LoadingStatus.Failed)
    }
  }

  return { loading, error, confirmPasswordReset }
}

export const useChangePassword = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const changePassword = async (
    password: string,
    onRequiresRecentLoginError: () => void
  ) => {
    setLoading(LoadingStatus.Pending)
    setError(null)

    try {
      await auth.updatePassword(password)
      setLoading(LoadingStatus.Succeeded)
    } catch (error: any) {
      if (error?.code === Errors.RequiresRecentLogin) {
        setLoading(LoadingStatus.Idle)
        onRequiresRecentLoginError()
        return
      }

      setError(error?.code ?? Errors.InternalError)
      setLoading(LoadingStatus.Failed)
    }
  }

  return { loading, error, changePassword }
}

export const useVerifyPasswordResetCode = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const verifyPasswordResetCode = async (code: string) => {
    try {
      setLoading(LoadingStatus.Pending)
      await auth.verifyPasswordResetCode(code)
      setLoading(LoadingStatus.Succeeded)
      setError(null)
      return
    } catch (error: any) {
      setError(error?.code || null)
      setLoading(LoadingStatus.Failed)
    }
  }

  return {
    loading,
    error,
    verifyPasswordResetCode,
  }
}

export const useSendPasswordResetEmail = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const sendPasswordResetEmail = async (email: string): Promise<boolean> => {
    setLoading(LoadingStatus.Pending)
    setError(null)

    try {
      await auth.sendPasswordResetEmail(email)
      setLoading(LoadingStatus.Succeeded)
      return true
    } catch (error: any) {
      if (
        error?.code === Errors.UserNotFound ||
        isPasswordResetEligibilityBlocked(error)
      ) {
        setLoading(LoadingStatus.Succeeded)
        return false
      }
      setError(error?.code ?? Errors.InternalError)
      setLoading(LoadingStatus.Failed)
      return false
    }
  }

  return { loading, error, sendPasswordResetEmail }
}

export const useAccountActivate = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const activateAccount = async (code: string) => {
    try {
      setLoading(LoadingStatus.Pending)
      await auth.activateAccount(code)
      setLoading(LoadingStatus.Succeeded)
      return
    } catch (error: any) {
      setError(error?.code || null)
      setLoading(LoadingStatus.Failed)
    }
  }

  return {
    loading,
    error,
    activateAccount,
  }
}

export const useCurrentUser = () => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(LoadingStatus.Pending)
    const unsubscribe = auth.onAuthStateChanged(authUser => {
      setUser(authUser)
      setLoading(LoadingStatus.Succeeded)
    })

    return () => {
      unsubscribe()
      setUser(null)
      setError(null)
      setLoading(LoadingStatus.Idle)
    }
  }, [])

  return { user, loading, error, auth }
}

export const useFetchSignInMethodsForEmail = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const fetchSignInMethodsForEmail = async (email: string) => {
    try {
      setLoading(LoadingStatus.Pending)
      const data = await auth.fetchSignInMethodsForEmail(email)
      // eslint-disable-next-line no-throw-literal
      if (data.length === 0) throw { code: Errors.AuthEmailNotFound }
      setLoading(LoadingStatus.Succeeded)
      return data
    } catch (error: any) {
      setError(error?.code || null)
      setLoading(LoadingStatus.Failed)
    }
  }

  return {
    loading,
    error,
    fetchSignInMethodsForEmail,
  }
}

export const useSendSignInLinkToEmail = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const sendSignInLinkToEmail = async (
    email: string,
    reauthenticate: boolean,
    reauthVariant: ReauthenticationVariant | null
  ) => {
    try {
      setLoading(LoadingStatus.Pending)
      if (reauthVariant !== null && reauthenticate) {
        if (reauthVariant === ReauthenticationVariant.SECURITY) {
          await auth.sendSignInLinkToEmailToReauthBeforeSecurityChange(email)
        } else {
          await auth.sendSignInLinkToEmailToReauthBeforDeleteAccount(email)
        }
      } else if (
        reauthVariant === ReauthenticationVariant.DELETE_ACCOUNT &&
        !reauthenticate
      ) {
        // login before delete account
        await auth.sendSignInLinkToEmailToLoginBeforDeleteAccount(email)
      } else {
        // basic email login
        await auth.sendSignInLinkToEmail(email)
      }
      window.localStorage.setItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY, email)
      setLoading(LoadingStatus.Succeeded)
      setError(null)
      return
    } catch (error: any) {
      setError(error?.code || null)
      setLoading(LoadingStatus.Failed)
    }
  }

  return {
    loading,
    error,
    sendSignInLinkToEmail,
  }
}

export const useIsSignInWithEmailLinkCheck = () => {
  const isSignInWithEmailLinkCheck = (emailLink: string) => {
    return auth.isSignInWithEmailLink(emailLink)
  }
  return { isSignInWithEmailLinkCheck }
}

export const useSignInWithOAuthProvider = () => {
  const { setLocalPreferencsByRemoteConfig } = useConsents()
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)
  const redirectToReauthenticationSuccess =
    useRedirectToReauthenticationSuccess()
  const { setError: setGlobalAuthError } = useAuthContext()
  const handleMultiFactorError = useMultiFactorErrorHandler()

  const finishSuccessfulSignIn = async (
    reauthenticate: boolean,
    reauthVariant: ReauthenticationVariant | null
  ) => {
    await auth.reloadUser()
    if (reauthVariant !== null) {
      redirectToReauthenticationSuccess(reauthVariant)
    }
    setLocalPreferencsByRemoteConfig()
    setLoading(LoadingStatus.Succeeded)
    setError(null)
  }

  const signInWithOAuthProvider = async (
    providerId: OAuthProviderId,
    reauthenticate = false,
    reauthVariant: ReauthenticationVariant | null = null
  ) => {
    try {
      setLoading(LoadingStatus.Pending)
      setError(null)
      if (setGlobalAuthError) setGlobalAuthError(null)

      if (reauthenticate && reauthVariant !== null) {
        await auth.reauthenticateWithOAuthProvider(providerId)
      } else {
        await auth.signInWithOAuthProvider(providerId)
      }

      await finishSuccessfulSignIn(reauthenticate, reauthVariant)
    } catch (error: any) {
      if (
        handleMultiFactorError(error, {
          onResolved: () =>
            finishSuccessfulSignIn(reauthenticate, reauthVariant),
        })
      ) {
        setLoading(LoadingStatus.Idle)
        return
      }

      if (isPopupClosedError(error?.code)) {
        await auth.reloadUser()
        const hasCurrentUser = !!auth.getCurrentUser()

        if (!reauthenticate && hasCurrentUser) {
          await finishSuccessfulSignIn(reauthenticate, reauthVariant)
          return
        }

        setLoading(LoadingStatus.Idle)
        return
      }

      console.error('OAuth sign-in failed:', providerId, error?.code, error)
      const code = isOAuthAccountNotFound(error)
        ? Errors.AuthEmailNotFound
        : error?.code || null
      setError(code)
      if (setGlobalAuthError && code) setGlobalAuthError(code)
      setLoading(LoadingStatus.Failed)
    }
  }

  return {
    loading,
    error,
    signInWithOAuthProvider,
  }
}

export const useDeleteUser = () => {
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const [error, setError] = useState<string | null>(null)

  const deleteUser = async () => {
    try {
      setLoading(LoadingStatus.Pending)
      await auth.deleteUser()
      setLoading(LoadingStatus.Succeeded)
      setError(null)
    } catch (err: any) {
      setError(err?.code || null)
      setLoading(LoadingStatus.Failed)
      throw err?.code || null
    }
  }

  return {
    loading,
    error,
    deleteUser,
  }
}
